import { useEffect, useRef, useState } from 'react';
import { ImagePlus, X } from 'lucide-react';
import { api } from '../../../../services/api';
import { uploadImage } from '../../../../services/storage';
import { useWorkspace } from '../../WorkspaceContext';

const MAX_COVER_BYTES = 5 * 1024 * 1024;

/**
 * Business contact details collected at onboarding — phone is used for SMS
 * alerts when someone books on the public booking page.
 */
const ManageProfile = () => {
  const { business, refresh } = useWorkspace();
  const fileInputRef = useRef(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverRemoved, setCoverRemoved] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setName(business?.name || '');
    setPhone(business?.phone || '');
    setCoverImageUrl(business?.coverImageUrl || null);
    setCoverPreview(null);
    setCoverFile(null);
    setCoverRemoved(false);
  }, [business?.id, business?.name, business?.phone, business?.coverImageUrl]);

  const displayCover = coverPreview || (!coverRemoved ? coverImageUrl : null);

  const handleCoverSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Choose an image file for the cover photo.');
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      setError('Cover photo must be 5MB or smaller.');
      return;
    }

    setError('');
    setCoverFile(file);
    setCoverRemoved(false);
    setCoverPreview(URL.createObjectURL(file));
    event.target.value = '';
  };

  const removeCover = () => {
    if (coverPreview) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverFile(null);
    setCoverPreview(null);
    setCoverRemoved(true);
  };

  const save = async (event) => {
    event.preventDefault();
    const nextName = name.trim();
    const nextPhone = phone.trim();

    if (!nextName) {
      setError('Business name is required.');
      return;
    }
    if (!nextPhone) {
      setError('Business phone is required for booking SMS alerts.');
      return;
    }

    setSaving(true);
    setError('');
    setSaved(false);

    try {
      let nextCoverUrl = coverRemoved ? null : coverImageUrl;
      if (coverFile) {
        const ext = coverFile.name.split('.').pop() || 'jpg';
        const path = `businesses/${business.id}/cover_${Date.now()}.${ext}`;
        nextCoverUrl = await uploadImage(coverFile, path);
      }

      const result = await api.updateBusiness({
        name: nextName,
        phone: nextPhone,
        coverImageUrl: nextCoverUrl,
      });

      if (result.error) {
        setError(result.error);
        setSaving(false);
        return;
      }

      setCoverImageUrl(nextCoverUrl);
      setCoverFile(null);
      if (coverPreview) {
        URL.revokeObjectURL(coverPreview);
        setCoverPreview(null);
      }
      setCoverRemoved(false);
      setSaved(true);
      await refresh();
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err?.message || 'Failed to save profile.');
    }

    setSaving(false);
  };

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h2 className="font-display text-2xl font-light text-ink-900">Business profile</h2>
        <p className="mt-1 text-sm text-ink-700/80">
          Contact details and cover photo used on your booking page and for SMS notifications when
          clients book online.
        </p>
      </div>

      {error && (
        <div className="border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <form className="space-y-5 bg-sand-50 p-5 ring-1 ring-ink-900/10 sm:p-6" onSubmit={save}>
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
              Cover photo <span className="font-normal normal-case tracking-normal text-ink-500">(optional)</span>
            </label>
            {displayCover && (
              <button
                type="button"
                onClick={removeCover}
                className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-red-700 hover:text-red-800"
              >
                <X className="h-3.5 w-3.5" />
                Remove
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="group relative block w-full overflow-hidden bg-white text-left ring-1 ring-inset ring-ink-900/15 transition-colors hover:bg-white/80 focus:outline-none focus:ring-2 focus:ring-saffron-500"
          >
            {displayCover ? (
              <img
                src={displayCover}
                alt="Business cover"
                className="h-40 w-full object-cover sm:h-48"
              />
            ) : (
              <div className="flex h-40 flex-col items-center justify-center gap-2 px-4 sm:h-48">
                <ImagePlus className="h-7 w-7 text-ink-500/50" />
                <span className="text-sm text-ink-700/70">Upload a wide cover for your booking page</span>
                <span className="text-xs text-ink-500/60">JPG or PNG · max 5MB</span>
              </div>
            )}
            {displayCover && (
              <span className="absolute inset-x-0 bottom-0 bg-ink-950/55 px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-sand-50 opacity-0 transition-opacity group-hover:opacity-100">
                Change cover
              </span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleCoverSelect}
          />
        </div>

        <div>
          <label
            htmlFor="business-name"
            className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
          >
            Business name <span className="text-saffron-600">*</span>
          </label>
          <input
            id="business-name"
            type="text"
            required
            autoComplete="organization"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-white px-4 py-3 text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
            placeholder="Golden Coil"
          />
        </div>

        <div>
          <label
            htmlFor="business-phone"
            className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700"
          >
            Business phone <span className="text-saffron-600">*</span>
          </label>
          <input
            id="business-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            placeholder="+233 24 000 0000"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full bg-white px-4 py-3 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500"
          />
          <p className="mt-2 text-xs text-ink-600/70">
            We text this number whenever someone books online, so you can prepare for the visit.
          </p>
        </div>

        {(business?.city || business?.country) && (
          <p className="text-sm text-ink-600/70">
            {[business.city, business.country].filter(Boolean).join(', ')}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-saffron-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {saved && <span className="text-sm text-emerald-700">Saved</span>}
        </div>
      </form>
    </div>
  );
};

export default ManageProfile;
